import { AfterViewChecked, AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UiButtonFunctionComponent, UiIconComponent, UiTabsHeaderComponent, UiTabsContentComponent, UiTabComponent } from '@cynteka/ui-kit';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, map, skip, switchMap, takeUntil } from 'rxjs/operators';

import { ChatController } from '../../core/chat.controller';
import { ChatManagerViewComponent } from '../chat-manager-view/chat-manager-view.component';

@Component({
    selector: 'chat-component',
    standalone: true,
    imports: [CommonModule, ChatManagerViewComponent, UiButtonFunctionComponent, UiIconComponent, UiTabsHeaderComponent, UiTabsContentComponent, UiTabComponent],
    templateUrl: './chat.component.html',
    styleUrls: ['./chat.component.scss'],
})
export class ChatComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
    /** Контроллер чата, управляющий менеджерами и сессиями. */
    @Input() public chatController!: ChatController;

    /** Событие закрытия компонента чата. */
    @Output() public closed = new EventEmitter<void>();

    /** Id открытого диалога активного менеджера, или null если диалог закрыт. */
    @Output() public activeSessionChange = new EventEmitter<string | null>();

    /** Позиция скролла списка сообщений. */
    @Output() public scrollPositionChange = new EventEmitter<number>();

    /** Сохранённая позиция скролла для восстановления. */
    @Input() public restoreScrollTop: number | null = null;

    @ViewChild('tabsContent') private tabsContent?: UiTabsContentComponent;
    @ViewChild(ChatManagerViewComponent) private managerView?: ChatManagerViewComponent;

    public isDraggingFiles = false;

    private readonly tabChangeSource = new EventEmitter<{ id: string; index: number }>();
    private readonly destroy$ = new Subject<void>();
    private tabChangeSub?: Subscription;
    private parentSet = false;

    /** @inheritdoc */
    public ngOnInit(): void {
        this.chatController.activeManager$.pipe(
            switchMap(manager => manager.activeSession$),
            map(session => session?.id ?? null),
            distinctUntilChanged(),
            skip(1),
            takeUntil(this.destroy$),
        ).subscribe(sessionId => this.activeSessionChange.emit(sessionId));
    }

    /** @inheritdoc */
    public ngAfterViewInit(): void {
        this.initTabsParent();
    }

    /** @inheritdoc */
    public ngAfterViewChecked(): void {
        this.initTabsParent();
    }

    /** @inheritdoc */
    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.tabChangeSub?.unsubscribe();
    }

    /**
     * Обрабатывает смену активного таба.
     * Выбирает соответствующего менеджера и при необходимости создаёт новую сессию.
     */
    public onTabChange(event: { id: string; index: number }): void {
        const manager = this.chatController.managers[event.index];
        this.chatController.selectManager(manager);
        // if (manager.template.config.emptyState === false) {
        //     manager.createSession();
        // }
    }

    public onDragOver(event: DragEvent): void {
        if (!event.dataTransfer?.types.includes('Files')) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        const input = this.managerView?.fileInput;
        this.isDraggingFiles = Boolean(input?.allowFiles && !input.isRecording);
        event.dataTransfer.dropEffect = this.isDraggingFiles ? 'copy' : 'none';
    }

    public onDragLeave(event: DragEvent): void {
        if (!(event.currentTarget as HTMLElement).contains(event.relatedTarget as Node | null)) {
            this.isDraggingFiles = false;
        }
    }

    public onDrop(event: DragEvent): void {
        this.isDraggingFiles = false;
        const files = Array.from(event.dataTransfer?.files ?? []);
        if (!files.length) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.managerView?.fileInput?.addFiles(files);
    }

    private initTabsParent(): void {
        if (this.tabsContent && !this.parentSet) {
            this.tabsContent.setParent({ tabChange: this.tabChangeSource } as any);
            this.tabChangeSub = this.tabChangeSource.subscribe((event) => this.onTabChange(event));
            this.parentSet = true;
        }
    }
}
